import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useDaily, useDailyEvent, DailyAudio, useParticipantIds } from "@daily-co/daily-react";
import VideoChat from "./components/VideoChat";
import TabButton from "./components/TabButton";
import { createAgentRoom, getAgent } from "./lib/LemonSliceClient";
import { useAgent } from "./providers/AgentProvider";
import { useWidget, ACTIVE_STATE, MINIMIZED_STATE, HIDDEN_STATE } from "./providers/WidgetProvider";
import { useError } from "./providers/ErrorProvider";
import { useMessage } from "./providers/MessageProvider";
import { useMute } from "./providers/MuteProvider";
import { useUserInteraction } from "./providers/UserInteractionProvider";
import { DailyEventObjectAppMessage, DailyEventObjectNetworkConnectionEvent } from "@daily-co/daily-js";

interface VideoChatWidgetProps {
  agentId?: string;
  isInline?: boolean;
}

export default function VideoChatWidget({ agentId, isInline }: VideoChatWidgetProps) {
  const { agentData, setAgentData } = useAgent();
  const {
    widgetState,
    setWidgetState,
    roomUrl,
    setRoomUrl,
    isTransitioning,
    isAboutToTransition,
    hasJoinedRoom,
    setHasJoinedRoom,
    resetDailyState,
    setIsAgentVideoReady,
  } = useWidget();
  const { startMaxVideoTimer, errorMsg, setErrorMsg, setNonFatalErrorMsg } = useError();
  const { resetInitMsg } = useMessage();
  const { isAudioMuted, isMicMuted } = useMute();
  const { hasUserInteracted } = useUserInteraction();
  const callObject = useDaily();
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const fetchingRoomUrl = useRef<boolean>(false);
  const joiningRoom = useRef<boolean>(false);
  const dailyAudioRef = useRef<any>(null);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [signalError, setSignalError] = useState<boolean>(false);

  // Fetch agent data on mount
  useEffect(() => {
    if (agentId) {
      getAgent(agentId)
        .then(data => {
          if (!data.sufficient_balance) {
            throw new Error("Insufficient balance");
          }
          setAgentData(data);
        })
        .catch(error => {
          console.error("Error fetching agent data:", error);
        });
    }
  }, [agentId, setAgentData]);

  // Fetch room URL when widget is opened for the first time
  useEffect(() => {
    if (widgetState === ACTIVE_STATE && hasUserInteracted && !fetchingRoomUrl.current && !roomUrl && agentId && !errorMsg) {
      fetchingRoomUrl.current = true;
      createAgentRoom(agentId)
        .then(response => {
          if (response.status === "success") {
            setRoomUrl(response.room_url);
          } else {
            setErrorMsg("An error occurred. Please try again.");
          }
        })
        .catch(error => {
          console.error("Error creating agent room:", error);
          setErrorMsg("An error occurred. Please try again.");
        })
        .finally(() => {
          fetchingRoomUrl.current = false;
        });
    }
  }, [widgetState, hasUserInteracted, agentId, roomUrl, errorMsg]);

  // Join or rejoin the call when widget opens and we have a room URL
  useEffect(() => {
    if (widgetState === ACTIVE_STATE && callObject && roomUrl && !hasJoinedRoom && !joiningRoom.current) {
      joiningRoom.current = true;
      // Join without requesting microphone permissions - audio will be enabled when user unmutes
      callObject
        .join({
          url: roomUrl,
          audioSource: true,
          videoSource: false,
          startAudioOff: isMicMuted,
          dailyConfig: {
            alwaysIncludeMicInPermissionPrompt: false,
          },
        })
        .then(() => {
          setHasJoinedRoom(true);
          resetInitMsg();
        })
        .catch(error => {
          console.error("Error joining call:", error);
          setErrorMsg("An error occurred. Please try again.");
          setHasJoinedRoom(false);
        })
        .finally(() => {
          joiningRoom.current = false;
        });
    }
  }, [widgetState, callObject, roomUrl, hasJoinedRoom, isMicMuted]);

  // Leave the call when the user leaves the widget for 30 seconds
  // Timer is controlled by the WidgetProvider
  useEffect(() => {
    if (callObject && !hasJoinedRoom) {
      callObject.leave();
    }
  }, [hasJoinedRoom, callObject]);

  // Handle pipecat messages
  useDailyEvent(
    "app-message",
    useCallback((ev: DailyEventObjectAppMessage) => {
      if (ev?.data?.type === "pipecat_shutdown") {
        setErrorMsg("An error occurred. Please try again.");
        resetDailyState();
      }
      if (ev?.data?.type === "bot_ready") {
        setIsAgentVideoReady(true);
      }
      if (ev?.data?.type === "gpu_error") {
        setErrorMsg("An error occurred. Please try again.");
        resetDailyState();
      }
    }, [])
  );

  // Handle agent joining the call - start video timer
  useDailyEvent(
    "participant-joined",
    useCallback(() => {
      startMaxVideoTimer();
    }, [])
  );

  // Handle Daily Errors
  useDailyEvent(
    "error",
    useCallback(() => {
      console.error("Encountered Daily error");
      setErrorMsg("An error occurred. Please try again.");
    }, [])
  );

  // Handle Agent leaving the call unexpectedly
  useDailyEvent(
    "participant-left",
    useCallback(() => {
      if (!errorMsg && hasJoinedRoom) {
        console.error("Agent left the call");
        setWidgetState(MINIMIZED_STATE);
        resetDailyState();
      }
    }, [hasJoinedRoom])
  );

  // Handle network connection events
  useDailyEvent(
    "network-connection",
    useCallback((ev: DailyEventObjectNetworkConnectionEvent) => {
      if (!ev) return;

      const connectionType = ev.type;
      const state = ev.event;
      if (state === "connected") {
        if (connectionType === "peer-to-peer" || connectionType === "sfu") {
          setNetworkError(false);
        } else if (connectionType === "signaling") {
          setSignalError(false);
        }
      } else if (state === "interrupted") {
        if (connectionType === "peer-to-peer" || connectionType === "sfu") {
          setNetworkError(true);
        } else if (connectionType === "signaling") {
          setSignalError(true);
        }
      }
    }, [])
  );

  useEffect(() => {
    if (signalError) {
      setNonFatalErrorMsg("Network issues.");
    } else if (networkError) {
      setNonFatalErrorMsg("Poor connection.");
    } else {
      setNonFatalErrorMsg("");
    }
  }, [networkError, signalError]);

  const handlePlayFailed = (error: Error) => {
    console.error("DailyAudio play failed:", error);
    setErrorMsg("An error occurred. Please try again.");
  };

  // Mute/unmute audio elements from DailyAudio
  useEffect(() => {
    if (dailyAudioRef.current) {
      // Get all audio elements from the DailyAudio component's DOM
      const audioElements = dailyAudioRef.current.getAllAudio();
      if (audioElements && audioElements.length > 0) {
        audioElements.forEach((audioElement: HTMLAudioElement) => {
          if (audioElement) {
            audioElement.muted = isAudioMuted;
          }
        });
      }
    }
  }, [isAudioMuted, remoteParticipantIds.length]);

  // Cleanup: Reset Daily state when component unmounts or user exits tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      resetDailyState();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      resetDailyState();
    };
  }, []);

  // Hide UI until agent data is loaded
  if (!agentData) {
    return null;
  }

  return (
    <div className={!isInline ? "fixed bottom-2 right-5 z-1000 flex flex-col items-end" : ""}>
      {(widgetState === HIDDEN_STATE || isTransitioning || isAboutToTransition) && <TabButton />}
      {(widgetState === ACTIVE_STATE || widgetState === MINIMIZED_STATE || isTransitioning || isAboutToTransition) && (
        <VideoChat />
      )}

      {widgetState !== HIDDEN_STATE && (
        <div
          className={
            "text-right text-[8px] text-white mix-blend-difference transition-opacity duration-150 shadow-2xs pr-[4px]"
          }
        >
          {!isInline && (
            <>
              Powered by{" "}
              <a href="https://lemonslice.com" target="_blank" rel="noopener noreferrer" className="underline">
                LemonSlice
              </a>
            </>
          )}
        </div>
      )}

      <DailyAudio ref={dailyAudioRef} onPlayFailed={handlePlayFailed} />
    </div>
  );
}
